/**
 * Rating History List Component
 *
 * Displays monthly rating entries for a fund/holding showing:
 * - Date (month/year)
 * - Rating status (In-Form, On-Track, Off-Track, Out-of-Form)
 * - Rank score and position
 * - Expandable history with show more/less
 *
 * Used in holding detail views to visualize rating changes over time.
 */

import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { ChevronDown, ChevronUp, TrendingUp, TrendingDown, Minus } from "lucide-react";
import type { FundRating, RankHistoryEntry } from "@curie/shared";

// ============================================================================
// Types
// ============================================================================

/**
 * Extended rating history entry with additional display data
 */
export interface RatingHistoryItem extends RankHistoryEntry {
  /** Optional score value (0-100) */
  score?: number;
}

/**
 * Props for the RatingHistoryList component
 */
export interface RatingHistoryListProps {
  /**
   * Array of rating history entries (sorted newest first)
   */
  history: RatingHistoryItem[];
  /**
   * Number of items to show initially before expanding
   * @default 3
   */
  initialDisplayCount?: number;
  /**
   * Maximum items to show when expanded
   * @default 12
   */
  maxDisplayCount?: number;
  /**
   * Whether to show the card wrapper
   * @default true
   */
  showCard?: boolean;
  /**
   * Optional title override
   * @default "Rating History"
   */
  title?: string;
  /**
   * Optional CSS class name
   */
  className?: string;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Status configuration with colors and labels
 */
const STATUS_CONFIG: Record<
  FundRating,
  {
    label: string;
    badgeBg: string;
    badgeText: string;
    dotColor: string;
  }
> = {
  "in-form": {
    label: "In-Form",
    badgeBg: "bg-emerald-100 dark:bg-emerald-900/30",
    badgeText: "text-emerald-700 dark:text-emerald-400",
    dotColor: "bg-emerald-500",
  },
  "on-track": {
    label: "On-Track",
    badgeBg: "bg-sky-100 dark:bg-sky-900/30",
    badgeText: "text-sky-700 dark:text-sky-400",
    dotColor: "bg-sky-500",
  },
  "off-track": {
    label: "Off-Track",
    badgeBg: "bg-amber-100 dark:bg-amber-900/30",
    badgeText: "text-amber-700 dark:text-amber-400",
    dotColor: "bg-amber-500",
  },
  "out-of-form": {
    label: "Out-of-Form",
    badgeBg: "bg-rose-100 dark:bg-rose-900/30",
    badgeText: "text-rose-700 dark:text-rose-400",
    dotColor: "bg-rose-500",
  },
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Format date for display (e.g., "Dec 2024")
 */
function formatMonthYear(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-IN", {
    month: "short",
    year: "numeric",
  });
}

/**
 * Calculate rank change between two entries
 */
function getRankChange(
  current: RatingHistoryItem,
  previous: RatingHistoryItem | undefined
): { direction: "up" | "down" | "same"; value: number } | null {
  if (!previous) return null;

  // Calculate percentile change (lower rank = better, so we invert)
  const currentPercentile = (current.rank / current.totalFunds) * 100;
  const previousPercentile = (previous.rank / previous.totalFunds) * 100;
  const change = previousPercentile - currentPercentile; // Positive = improvement

  if (Math.abs(change) < 1) {
    return { direction: "same", value: 0 };
  }

  return {
    direction: change > 0 ? "up" : "down",
    value: Math.abs(Math.round(change)),
  };
}

// ============================================================================
// Subcomponents
// ============================================================================

/**
 * Status badge for rating
 */
function StatusBadge({ rating }: { rating: FundRating }) {
  const config = STATUS_CONFIG[rating];
  return (
    <Badge
      variant="secondary"
      className={cn(
        "font-medium border-0 text-xs",
        config.badgeBg,
        config.badgeText
      )}
    >
      {config.label}
    </Badge>
  );
}

/**
 * Rank change indicator
 */
function RankChangeIndicator({
  change,
}: {
  change: { direction: "up" | "down" | "same"; value: number } | null;
}) {
  if (!change) return null;

  if (change.direction === "same") {
    return (
      <div className="flex items-center gap-0.5 text-muted-foreground">
        <Minus className="h-3 w-3" />
        <span className="text-xs">--</span>
      </div>
    );
  }

  const isImprovement = change.direction === "up";
  return (
    <div
      className={cn(
        "flex items-center gap-0.5",
        isImprovement ? "text-green-600" : "text-red-600"
      )}
    >
      {isImprovement ? (
        <TrendingUp className="h-3 w-3" />
      ) : (
        <TrendingDown className="h-3 w-3" />
      )}
      <span className="text-xs font-medium">
        {change.value}%
      </span>
    </div>
  );
}

/**
 * Individual history row item
 */
function HistoryRow({
  item,
  previousItem,
  isFirst,
  isLast,
}: {
  item: RatingHistoryItem;
  previousItem: RatingHistoryItem | undefined;
  isFirst: boolean;
  isLast: boolean;
}) {
  const config = STATUS_CONFIG[item.rating];
  const rankChange = getRankChange(item, previousItem);
  const percentile = ((item.rank / item.totalFunds) * 100).toFixed(0);

  return (
    <div
      className={cn(
        "flex items-center gap-3 py-3",
        !isLast && "border-b border-border/50"
      )}
    >
      {/* Timeline dot and line */}
      <div className="relative flex flex-col items-center">
        <div className={cn(config.dotColor, "h-2.5 w-2.5 rounded-full z-10")} />
        {!isLast && (
          <div className="absolute top-3 w-px h-full bg-border/50" />
        )}
      </div>

      {/* Date */}
      <div className="w-20 flex-shrink-0">
        <span className="text-sm font-medium text-foreground">
          {formatMonthYear(item.date)}
        </span>
      </div>

      {/* Status Badge */}
      <div className="flex-shrink-0">
        <StatusBadge rating={item.rating} />
      </div>

      {/* Rank Position */}
      <div className="flex-1 flex items-center justify-end gap-4">
        {/* Rank change indicator */}
        <RankChangeIndicator change={rankChange} />

        {/* Rank display */}
        <div className="flex items-baseline gap-1 text-right min-w-[60px]">
          <span className="text-sm font-semibold text-foreground">
            #{item.rank}
          </span>
          <span className="text-xs text-muted-foreground">
            / {item.totalFunds}
          </span>
        </div>

        {/* Score (if available) */}
        {item.score !== undefined && (
          <div className="w-12 text-right">
            <span className="text-sm font-medium text-foreground">
              {item.score}
            </span>
            <span className="text-xs text-muted-foreground">/100</span>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Empty state when no history available
 */
function EmptyState() {
  return (
    <div className="py-8 text-center">
      <p className="text-sm text-muted-foreground">
        No rating history available
      </p>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * Rating History List
 *
 * Displays a chronological list of rating entries for a fund, showing
 * how the rating and rank have changed over time.
 *
 * @example
 * ```tsx
 * <RatingHistoryList
 *   history={[
 *     { date: "2024-12-01", rank: 3, totalFunds: 45, rating: "in-form" },
 *     { date: "2024-11-01", rank: 5, totalFunds: 44, rating: "in-form" },
 *     { date: "2024-10-01", rank: 8, totalFunds: 44, rating: "on-track" },
 *   ]}
 * />
 * ```
 *
 * @example Without card wrapper
 * ```tsx
 * <RatingHistoryList
 *   history={history}
 *   showCard={false}
 *   initialDisplayCount={5}
 * />
 * ```
 */
export function RatingHistoryList({
  history,
  initialDisplayCount = 3,
  maxDisplayCount = 12,
  showCard = true,
  title = "Rating History",
  className,
}: RatingHistoryListProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Determine how many items to show
  const hasMore = history.length > initialDisplayCount;
  const displayCount = isExpanded
    ? Math.min(history.length, maxDisplayCount)
    : Math.min(history.length, initialDisplayCount);
  const displayedItems = history.slice(0, displayCount);
  const remainingCount = Math.min(history.length, maxDisplayCount) - displayCount;

  // Handle toggle
  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  // Content to render
  const content = (
    <>
      {history.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="space-y-0">
          {/* History items */}
          {displayedItems.map((item, index) => (
            <HistoryRow
              key={item.date}
              item={item}
              previousItem={displayedItems[index + 1]}
              isFirst={index === 0}
              isLast={index === displayedItems.length - 1 && !hasMore}
            />
          ))}

          {/* Expand/Collapse button */}
          {hasMore && (
            <div className="pt-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleToggle}
                className="w-full text-muted-foreground hover:text-foreground"
              >
                {isExpanded ? (
                  <>
                    <ChevronUp className="h-4 w-4 mr-1" />
                    Show Less
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4 mr-1" />
                    Show {remainingCount} More
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );

  // Render with or without card wrapper
  if (!showCard) {
    return <div className={cn(className)}>{content}</div>;
  }

  return (
    <Card className={cn(className)}>
      <CardHeader>
        <CardTitle className="text-lg font-semibold">{title}</CardTitle>
      </CardHeader>
      <CardContent>{content}</CardContent>
    </Card>
  );
}

// ============================================================================
// Skeleton Loader
// ============================================================================

/**
 * Skeleton loader for RatingHistoryList
 */
export function RatingHistoryListSkeleton({
  itemCount = 3,
  showCard = true,
  className,
}: {
  itemCount?: number;
  showCard?: boolean;
  className?: string;
}) {
  const skeletonRows = (
    <div className="space-y-0">
      {[...Array(itemCount)].map((_, index) => (
        <div
          key={index}
          className={cn(
            "flex items-center gap-3 py-3",
            index < itemCount - 1 && "border-b border-border/50"
          )}
        >
          {/* Timeline dot */}
          <Skeleton className="h-2.5 w-2.5 rounded-full flex-shrink-0" />

          {/* Date */}
          <Skeleton className="h-4 w-16 flex-shrink-0" />

          {/* Status badge */}
          <Skeleton className="h-5 w-16 rounded-full flex-shrink-0" />

          {/* Rank and score */}
          <div className="flex-1 flex items-center justify-end gap-4">
            <Skeleton className="h-3 w-8" />
            <Skeleton className="h-4 w-16" />
          </div>
        </div>
      ))}
    </div>
  );

  if (!showCard) {
    return <div className={cn(className)}>{skeletonRows}</div>;
  }

  return (
    <Card className={cn(className)}>
      <CardHeader>
        <Skeleton className="h-6 w-32" />
      </CardHeader>
      <CardContent>{skeletonRows}</CardContent>
    </Card>
  );
}

export default RatingHistoryList;
